import type { Meta } from '@cubejs-client/core'
import {
  Bold,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Plus,
  Redo2,
  Table2,
  Undo2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useEditorState, type Editor } from '@tiptap/react'

import { OptionSelect } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { isAllowedHref } from '@/lib/report-document'
import { messages } from '@/lib/messages'

import { buildInsert, INSERT_KINDS, insertNode, insideRepeat, type InsertKind } from './insert'
import type { DocumentTemplate } from './serialize'

const t = messages.documents.editor

function ToolButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <Button
      type="button"
      variant={active ? 'secondary' : 'ghost'}
      size="icon-sm"
      aria-label={label}
      title={label}
      aria-pressed={active}
      disabled={disabled}
      // Düğme odağı çalmasın: seçim ve imleç editörde kalır.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </Button>
  )
}

function LinkButton({ editor, active }: { editor: Editor; active: boolean }) {
  const [open, setOpen] = useState(false)
  const [href, setHref] = useState('')
  const [error, setError] = useState(false)

  const apply = () => {
    if (!isAllowedHref(href)) return setError(true)
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setHref(String(editor.getAttributes('link').href ?? ''))
          setError(false)
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger
        render={
          <Button
            type="button"
            variant={active ? 'secondary' : 'ghost'}
            size="icon-sm"
            aria-label={t.toolbar.link}
            title={t.toolbar.link}
            aria-pressed={active}
          />
        }
      >
        <LinkIcon />
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-72 flex-col gap-2 p-2">
        <Input
          aria-label={t.toolbar.link}
          placeholder={t.link.placeholder}
          value={href}
          aria-invalid={error || undefined}
          onChange={(event) => {
            setHref(event.target.value)
            setError(false)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') apply()
          }}
        />
        {error && (
          <p role="alert" className="text-destructive text-xs">
            {t.link.invalid}
          </p>
        )}
        <div className="flex gap-2">
          <Button size="sm" onClick={apply}>
            {t.link.apply}
          </Button>
          {active && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                setOpen(false)
              }}
            >
              {t.link.remove}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function InsertMenu({
  editor,
  meta,
  template,
}: {
  editor: Editor
  meta: Meta | undefined
  template: DocumentTemplate
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button type="button" variant="outline" size="sm" />}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Plus />
        {t.insert.menu}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        {INSERT_KINDS.map((kind: InsertKind) => {
          const nested = kind === 'repeat' && insideRepeat(editor)
          const built = nested
            ? ({ ok: false, reason: 'nestedRepeat' } as const)
            : buildInsert(kind, meta, template)
          return (
            <DropdownMenuItem
              key={kind}
              disabled={!built.ok}
              onClick={() => insertNode(editor, kind, meta, template)}
            >
              <span className="flex flex-col">
                <span>{t.insert[kindLabel(kind)]}</span>
                {!built.ok && (
                  <span className="text-muted-foreground text-xs">{t.insert[built.reason]}</span>
                )}
              </span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Düğüm adı -> menü etiketi anahtarı. */
function kindLabel(kind: InsertKind) {
  switch (kind) {
    case 'reportKpi':
      return 'kpi'
    case 'reportTable':
      return 'table'
    case 'reportList':
      return 'list'
    case 'reportChart':
      return 'chart'
    default:
      return kind
  }
}

/**
 * Şablon editöründe tam araç çubuğu (tablo + Ekle menüsü). 	emplate verilmezse yalnız metin
 * araçları gelir (dönem kopyası: widget/tablo eklenmez); children sağdaki ek eylemler içindir.
 */
export function EditorToolbar({
  editor,
  meta,
  template,
  children,
}: {
  editor: Editor
  meta?: Meta | undefined
  template?: DocumentTemplate
  children?: ReactNode
}) {
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive('bold'),
      italic: current.isActive('italic'),
      bulletList: current.isActive('bulletList'),
      orderedList: current.isActive('orderedList'),
      link: current.isActive('link'),
      heading: [1, 2, 3].find((level) => current.isActive('heading', { level })) ?? 0,
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
    }),
  })

  return (
    <div
      role="toolbar"
      aria-label={t.toolbar.label}
      className="border-border bg-card flex flex-wrap items-center gap-1 rounded-md border p-1"
    >
      <OptionSelect
        size="sm"
        aria-label={t.toolbar.paragraph}
        className="w-auto"
        value={String(state.heading)}
        options={[
          { value: '0', label: t.toolbar.paragraph },
          ...[1, 2, 3].map((level) => ({ value: String(level), label: t.toolbar.heading(level) })),
        ]}
        onValueChange={(next) => {
          const level = Number(next)
          const chain = editor.chain().focus()
          if (level === 0) chain.setParagraph().run()
          else chain.setHeading({ level: level as 1 | 2 | 3 }).run()
        }}
      />
      <ToolButton
        label={t.toolbar.bold}
        active={state.bold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold />
      </ToolButton>
      <ToolButton
        label={t.toolbar.italic}
        active={state.italic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </ToolButton>
      <ToolButton
        label={t.toolbar.bulletList}
        active={state.bulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List />
      </ToolButton>
      <ToolButton
        label={t.toolbar.orderedList}
        active={state.orderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered />
      </ToolButton>
      {template && (
        <ToolButton
          label={t.toolbar.table}
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          <Table2 />
        </ToolButton>
      )}
      <LinkButton editor={editor} active={state.link} />
      <span className="bg-border mx-1 h-5 w-px" aria-hidden="true" />
      <ToolButton
        label={t.toolbar.undo}
        disabled={!state.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 />
      </ToolButton>
      <ToolButton
        label={t.toolbar.redo}
        disabled={!state.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 />
      </ToolButton>
      {children}
      {template && (
        <div className="ml-auto">
          <InsertMenu editor={editor} meta={meta} template={template} />
        </div>
      )}
    </div>
  )
}
